import type { RewardOffer } from '../engine/types.js';
import {
  rewardBonusDescription,
  rewardBonusTitle,
} from '../engine/rewards.js';
import { el, button, clear } from './dom.js';

const QUALITY_LABEL: Record<string, string> = {
  small: 'Petit',
  standard: 'Standard',
  large: 'Grand',
};

export function showRewardModal(
  root: HTMLElement,
  offers: RewardOffer[],
  week: number,
  onPick: (index: number) => void,
): void {
  // Avoid stacking duplicate week-reward overlays
  if (root.querySelector('.reward-modal')) return;
  removeModals(root);
  const overlay = el('div', 'modal-overlay');
  const modal = el('div', 'modal reward-modal');
  modal.append(
    el('h2', 'modal-title', [`Récompense — Semaine ${week}`]),
    el('p', 'modal-sub', [week === 1 ? 'Première semaine : bonus garanti !' : 'Choisissez une carte']),
  );
  const row = el('div', 'reward-cards');
  let picked = false;
  offers.forEach((offer, i) => {
    const card = el('button', `reward-card quality-${offer.quality}`);
    card.append(
      el('span', 'reward-quality', [QUALITY_LABEL[offer.quality] ?? offer.quality]),
      el('span', 'reward-cables', [`+${offer.cables} câbles`]),
      el('span', 'reward-bonus', [rewardBonusTitle(offer)]),
      el('span', 'reward-bonus-desc', [rewardBonusDescription(offer)]),
    );
    card.addEventListener('pointerup', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (picked) return;
      picked = true;
      card.classList.add('picked');
      onPick(i);
      // Remove after pick so the click cannot fall through onto the canvas.
      requestAnimationFrame(() => overlay.remove());
    });
    row.append(card);
  });
  modal.append(row);
  overlay.append(modal);
  root.append(overlay);
}

export function showPauseMenu(
  root: HTMLElement,
  data: { fragmentsIfAbandon: number },
  actions: {
    continue: () => void;
    abandon: () => void;
    settings?: () => void;
  },
): void {
  removeModals(root);
  const overlay = el('div', 'modal-overlay pause-overlay');
  const modal = el('div', 'modal pause-modal');
  modal.append(
    el('h2', 'modal-title', ['Pause']),
    el('p', 'modal-sub', [
      `Abandonner maintenant : +${data.fragmentsIfAbandon} fragments`,
    ]),
  );
  const actionsEl = el('div', 'modal-actions');
  actionsEl.append(
    button('Continuer', 'btn primary', () => {
      overlay.remove();
      actions.continue();
    }),
  );
  if (actions.settings) {
    actionsEl.append(
      button('Réglages', 'btn', () => {
        overlay.remove();
        actions.settings!();
      }),
    );
  }
  actionsEl.append(
    button('Abandonner', 'btn danger', () => {
      overlay.remove();
      actions.abandon();
    }),
  );
  modal.append(actionsEl);
  overlay.append(modal);
  root.append(overlay);
}

/** Compact settings panel usable from the in-game pause menu. */
export function showInGameSettings(
  root: HTMLElement,
  settings: {
    musicVolume: number;
    sfxVolume: number;
    particles: boolean;
    glow: boolean;
    showGrid: boolean;
    colorblindSymbols: boolean;
  },
  onChange: (next: typeof settings) => void,
  onBack: () => void,
): void {
  removeModals(root);
  const overlay = el('div', 'modal-overlay pause-overlay');
  const modal = el('div', 'modal pause-modal');
  modal.append(el('h2', 'modal-title', ['Réglages']));

  const mkRange = (label: string, key: 'musicVolume' | 'sfxVolume') => {
    const wrap = el('label', 'setting-row');
    wrap.append(el('span', '', [label]));
    const input = el('input') as HTMLInputElement;
    input.type = 'range';
    input.min = '0';
    input.max = '1';
    input.step = '0.05';
    input.value = String(settings[key]);
    input.addEventListener('input', () => {
      settings[key] = Number(input.value);
      onChange({ ...settings });
    });
    wrap.append(input);
    return wrap;
  };

  modal.append(mkRange('Musique', 'musicVolume'), mkRange('Effets', 'sfxVolume'));

  for (const [label, key] of [
    ['Particules', 'particles'],
    ['Lueur', 'glow'],
    ['Grille', 'showGrid'],
    ['Symboles daltoniens', 'colorblindSymbols'],
  ] as const) {
    const wrap = el('label', 'setting-row');
    const input = el('input') as HTMLInputElement;
    input.type = 'checkbox';
    input.checked = !!settings[key];
    input.addEventListener('change', () => {
      settings[key] = input.checked;
      onChange({ ...settings });
    });
    wrap.append(input, el('span', '', [label]));
    modal.append(wrap);
  }

  const actionsEl = el('div', 'modal-actions');
  actionsEl.append(
    button('Retour', 'btn primary', () => {
      overlay.remove();
      onBack();
    }),
  );
  modal.append(actionsEl);
  overlay.append(modal);
  root.append(overlay);
}

/**
 * Compact bar after collapse — network stays fully visible underneath.
 * Call `showGameOverModal` when the player asks for results.
 */
export function showGameOverAdmireBar(
  root: HTMLElement,
  data: { day: number; score: number },
  onResults: () => void,
): void {
  if (root.querySelector('.gameover-admire')) return;
  const host = root.querySelector('.game-screen') ?? root;
  const bar = el('div', 'gameover-admire');
  const copy = el('div', 'gameover-admire-copy');
  copy.append(
    el('strong', 'gameover-admire-title', ['Réseau effondré']),
    el('span', 'gameover-admire-stats', [
      `Jour ${data.day} · Score ${Math.round(data.score)}`,
    ]),
    el('span', 'gameover-admire-hint', ['Admirez votre réseau']),
  );
  bar.append(copy, button('Résultats', 'btn primary', onResults));
  host.append(bar);
}

export function removeGameOverAdmireBar(root: HTMLElement): void {
  root.querySelectorAll('.gameover-admire').forEach((n) => n.remove());
}

export function showGameOverModal(
  root: HTMLElement,
  data: { day: number; score: number; fragments: number },
  actions: { replay: () => void; shop: () => void; maps: () => void; menu: () => void },
): void {
  if (root.querySelector('.gameover-modal')) return;
  removeGameOverAdmireBar(root);
  const overlay = el('div', 'modal-overlay gameover-overlay');
  const modal = el('div', 'modal gameover-modal');
  modal.append(
    el('h2', 'modal-title danger-text', ['Réseau effondré']),
    el('p', 'stat-line', [`Jour ${data.day}`]),
    el('p', 'stat-line', [`Score ${Math.round(data.score)}`]),
    el('p', 'stat-line highlight', [`+${data.fragments} fragments`]),
  );
  const actionsEl = el('div', 'modal-actions');
  actionsEl.append(
    button('Rejouer', 'btn primary', actions.replay),
    button('Boutique', 'btn', actions.shop),
    button('Cartes', 'btn', actions.maps),
    button('Menu', 'btn ghost', actions.menu),
  );
  modal.append(actionsEl);
  overlay.append(modal);
  root.append(overlay);
}

export function showAuthModal(
  root: HTMLElement,
  api: {
    login: (u: string, p: string) => Promise<{ ok: boolean; error?: string }>;
    register: (u: string, p: string, email?: string) => Promise<{ ok: boolean; error?: string }>;
    sso: () => void;
  },
  onClose: () => void,
): void {
  const overlay = el('div', 'modal-overlay');
  const modal = el('div', 'modal auth-modal');
  const err = el('p', 'auth-error hidden', ['']);
  const user = el('input', 'input') as HTMLInputElement;
  user.placeholder = 'Pseudo (3–30 caractères)';
  user.autocomplete = 'username';
  const email = el('input', 'input') as HTMLInputElement;
  email.type = 'email';
  email.placeholder = 'E-mail (optionnel)';
  email.autocomplete = 'email';
  const pass = el('input', 'input') as HTMLInputElement;
  pass.type = 'password';
  pass.placeholder = 'Mot de passe (min. 4)';
  pass.autocomplete = 'current-password';

  const setErr = (msg: string) => {
    err.textContent = msg;
    err.classList.toggle('hidden', !msg);
  };

  const authMessage = (r: { ok: boolean; error?: string }, fallback: string) => {
    if (r.error === 'offline') return 'Mode hors ligne — profil local';
    if (r.error && r.error !== 'login_failed' && r.error !== 'register_failed') return r.error;
    return fallback;
  };

  modal.append(
    el('h2', 'modal-title', ['Connexion / Inscription']),
    user,
    email,
    pass,
    err,
    button('Se connecter', 'btn primary', async () => {
      setErr('');
      if (!user.value.trim() || !pass.value) {
        setErr('Pseudo et mot de passe requis.');
        return;
      }
      const r = await api.login(user.value, pass.value);
      if (r.ok) {
        overlay.remove();
        onClose();
      } else setErr(authMessage(r, 'Échec connexion'));
    }),
    button('Créer un compte', 'btn', async () => {
      setErr('');
      if (!user.value.trim() || !pass.value) {
        setErr('Pseudo et mot de passe requis.');
        return;
      }
      const r = await api.register(user.value, pass.value, email.value);
      if (r.ok) {
        overlay.remove();
        onClose();
      } else setErr(authMessage(r, 'Inscription impossible'));
    }),
    button('SSO MusicGuess', 'btn accent', () => api.sso()),
    button('Fermer', 'btn ghost', () => {
      overlay.remove();
      onClose();
    }),
  );
  overlay.append(modal);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  root.append(overlay);
}

export function bonusLabel(offer: RewardOffer): string {
  return `${rewardBonusTitle(offer)} — +${offer.cables} câbles`;
}

export function removeModals(root: HTMLElement): void {
  root.querySelectorAll('.modal-overlay').forEach((n) => n.remove());
  removeGameOverAdmireBar(root);
}

export function showTutorialToast(root: HTMLElement, text: string): HTMLElement {
  const t = el('div', 'tutorial-toast', [text]);
  root.append(t);
  return t;
}

export function clearTutorial(root: HTMLElement): void {
  root.querySelectorAll('.tutorial-toast').forEach((n) => n.remove());
}

export function mountEmptyScreen(root: HTMLElement, className: string): HTMLElement {
  clear(root);
  const screen = el('div', `screen ${className}`);
  root.append(screen);
  return screen;
}
